import { ThemeConfig } from '../../__styling/themes'
import {
    BreakpointState,
    calcRem,
    PerthNowTheme,
    PerthNowThemeConfig,
    SevenNewsTheme,
    SevenNewsThemeConfig,
    TheNightlyTheme,
    TheNightlyThemeConfig,
    TheWestTheme,
    TheWestThemeConfig,
} from '../../__styling'
import {
    FontWeightAbsolute,
    OverflowWrapProperty,
    TextDecorationProperty,
    TextTransformProperty,
    WordBreakProperty,
} from 'csstype'

// COLOR IS DELIBERATELY EXCLUDED FROM THIS!
// COLOR SHOULD BE GROUPED WITH THE CONTAINER'S BACKGROUND COLOR FOR CONTRAST REASONS!!!!!!
type TextMixin = {
    fontFamily: string
    fontSize: string
    lineHeight: string
    fontWeight: FontWeightAbsolute
    letterSpacing?: string
    textDecoration?: TextDecorationProperty<string>
    textTransform?: TextTransformProperty
    wordBreak?: WordBreakProperty
    overflowWrap?: OverflowWrapProperty
}

export type TextMixinTheme<T extends ThemeConfig> = Record<
    T['textType'],
    BreakpointState<T, TextMixin>
>

/** Refer to https://www.figma.com/file/gLOszYPbdR4A5D1y3wI1SR/%5BTWDS%5D-The-West-Design-System?type=design&node-id=203-5566&mode=dev */
export type TheWestTextTypes =
    | 'head-1'
    | 'head-2'
    | 'head-3'
    | 'head-4'
    | 'head-5'
    | 'head-6'
    | 'head-7'
    | 'head-8'
    | 'head-9'
    | 'bold/32'
    | 'bold/15'
    | 'bold/13'
    | 'semi-bold/24'
    | 'medium/14'
    | 'medium/13'
    | 'body'

/** Refer to https://www.figma.com/file/gLOszYPbdR4A5D1y3wI1SR/%5BTWDS%5D-The-West-Design-System?type=design&node-id=203-5566&mode=dev */
export const theWestTextMixinTheme = (scheme: TheWestTheme) => {
    const theme: TextMixinTheme<TheWestThemeConfig> = {
        'head-1': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(39),
                lineHeight: calcRem(48),
            },
        },
        'head-2': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(32),
                lineHeight: calcRem(40),
            },
        },
        'head-3': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(22),
                lineHeight: calcRem(24),
            },
        },
        'head-4': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(16),
                lineHeight: calcRem(18),
            },
        },
        'head-5': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(16),
                lineHeight: calcRem(16),
                textTransform: 'uppercase',
            },
        },
        'head-6': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(15),
                lineHeight: calcRem(16),
            },
        },
        'head-7': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(14),
                lineHeight: calcRem(16),
            },
        },
        'head-8': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(12),
                lineHeight: calcRem(16),
            },
        },
        'head-9': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(12),
                lineHeight: calcRem(16),
            },
        },
        'bold/32': {
            default: {
                fontFamily: scheme.fonts.sansSerifCond,
                fontWeight: 'bold',
                fontSize: calcRem(32),
                lineHeight: calcRem(32),
                textTransform: 'uppercase',
            },
        },
        'bold/15': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'bold',
                fontSize: calcRem(15),
                lineHeight: calcRem(16),
            },
        },
        'bold/13': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'bold',
                fontSize: calcRem(13),
                lineHeight: calcRem(16),
            },
        },
        'semi-bold/24': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 600,
                fontSize: calcRem(24),
                lineHeight: calcRem(24),
            },
        },
        'medium/14': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 500,
                fontSize: calcRem(14),
                lineHeight: calcRem(16),
            },
        },
        'medium/13': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 500,
                fontSize: calcRem(13),
                lineHeight: calcRem(16),
            },
        },
        body: {
            default: {
                fontFamily: scheme.fonts.body,
                fontWeight: 'normal',
                fontSize: calcRem(16),
                lineHeight: calcRem(24),
            },
        },
    }

    return theme
}

/** Refer to https://www.figma.com/file/zSgfEjEwPSgMukUxkrseJs/%5BPNDS%5D-Perth-Now-Design-System?type=design&node-id=124-4482&mode=dev */
export type PerthNowTextTypes =
    | 'head-1'
    | 'head-2'
    | 'head-3'
    | 'head-4'
    | 'head-5'
    | 'head-6'
    | 'head-7'
    | 'head-8'
    | 'head-9'
    | 'head-10'
    | 'bold/14'
    | 'bold/13'
    | 'semi-bold/16'
    | 'semi-bold/14'
    | 'semi-bold/13'
    | 'medium/14'
    | 'medium/13'
    | 'medium/12'
    | 'regular/16'
    | 'regular/14'
    | 'body'

export const perthNowTextMixinTheme = function (scheme: PerthNowTheme) {
    const theme: TextMixinTheme<PerthNowThemeConfig> = {
        'head-1': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(42),
                lineHeight: calcRem(48),
            },
        },
        'head-2': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(36),
                lineHeight: calcRem(40),
            },
        },
        'head-3': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(30),
                lineHeight: calcRem(32),
            },
        },
        'head-4': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(26),
                lineHeight: calcRem(32),
            },
        },
        'head-5': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(24),
                lineHeight: calcRem(24),
            },
        },
        'head-6': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(22),
                lineHeight: calcRem(24),
            },
        },
        'head-7': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(14),
                lineHeight: calcRem(16),
            },
        },
        'head-8': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(12),
                lineHeight: calcRem(16),
            },
        },
        'head-9': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(12),
                lineHeight: calcRem(16),
            },
        },
        'head-10': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(18),
                lineHeight: calcRem(20),
            },
        },
        'bold/14': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'bold',
                fontSize: calcRem(14),
                lineHeight: calcRem(16),
            },
        },
        'bold/13': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'bold',
                fontSize: calcRem(13),
                lineHeight: calcRem(15),
            },
        },
        'semi-bold/16': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 600,
                fontSize: calcRem(16),
                lineHeight: calcRem(16),
            },
        },
        'semi-bold/14': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 600,
                fontSize: calcRem(14),
                lineHeight: calcRem(16),
            },
        },
        'semi-bold/13': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 600,
                fontSize: calcRem(13),
                lineHeight: calcRem(15),
            },
        },
        'medium/14': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(14),
                lineHeight: calcRem(18),
            },
        },
        'medium/13': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(13),
                lineHeight: calcRem(15),
            },
        },
        'medium/12': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(12),
                lineHeight: calcRem(14),
            },
        },
        'regular/16': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(16),
                lineHeight: calcRem(16),
            },
        },
        'regular/14': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(14),
                lineHeight: calcRem(16),
            },
        },
        body: {
            default: {
                fontFamily: scheme.fonts.body,
                fontWeight: 'normal',
                fontSize: calcRem(16),
                lineHeight: calcRem(24),
            },
        },
    }

    return theme
}

/** Source unknown. */
export type SevenNewsTextTypes = 'header'

/** Source unknown. */
export const sevenNewsTextMixinTheme = (scheme: SevenNewsTheme) => {
    const theme: TextMixinTheme<SevenNewsThemeConfig> = {
        header: {
            default: {
                fontFamily:
                    scheme.fonts.headingAlternative ?? scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(20),
                lineHeight: calcRem(30),
            },
        },
    }
    return theme
}

/** Refer to https://www.figma.com/file/5sdp8DaWM6vq6yqykGfVe1/The-Nightly-Design-System-(name-TBD)?type=design&node-id=6-404&mode=dev */
export type TheNightlyTextTypes =
    | 'section-header'
    | 'promo-header'
    | 'article-main'
    | 'article'
    | 'subheading'
    | 'item'
    | 'item-small'
    | 'body-main'
    | 'body-alt'
    | 'link'
    | 'byline-primary'
    | 'byline-secondary'
    | 'button-label'
    | 'byline-primary-large'
    | 'menu-item'

/** Refer to https://www.figma.com/file/5sdp8DaWM6vq6yqykGfVe1/The-Nightly-Design-System-(name-TBD)?type=design&node-id=6-404&mode=dev */
export const theNightlyTextMixinTheme = (scheme: TheNightlyTheme) => {
    const theme: TextMixinTheme<TheNightlyThemeConfig> = {
        'body-main': {
            default: {
                fontFamily: scheme.fonts.body,
                fontWeight: 'normal',
                fontSize: calcRem(20),
                lineHeight: '150%',
            },
        },
        link: {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(16),
                lineHeight: '150%',
                textDecoration: 'underline',
            },
        },
        'section-header': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(40),
                lineHeight: calcRem(48),
                letterSpacing: '-1.6px',
            },
            sm: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(45),
                lineHeight: calcRem(48),
                letterSpacing: '-1.8px',
            },
            lg: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(51),
                lineHeight: calcRem(56),
                letterSpacing: '-2.04px',
            },
        },
        'promo-header': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(32),
                lineHeight: calcRem(40),
            },
            sm: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(36),
                lineHeight: calcRem(40),
            },
            lg: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'normal',
                fontSize: calcRem(40),
                lineHeight: calcRem(52),
            },
        },
        'article-main': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(40),
                lineHeight: calcRem(44),
                letterSpacing: '-1.2px',
            },
            sm: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(45),
                lineHeight: calcRem(48),
                letterSpacing: '-1.35px',
            },
            lg: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(40),
                lineHeight: calcRem(48),
                letterSpacing: '-1.53px',
            },
        },
        article: {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(32),
                lineHeight: calcRem(38.4),
                letterSpacing: '-1.28px',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
            },
            sm: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(36),
                lineHeight: calcRem(40),
                letterSpacing: '-1.44px',
            },
            lg: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(40),
                lineHeight: calcRem(48),
                letterSpacing: '-1.6px',
            },
        },
        subheading: {
            default: {
                fontFamily: scheme.fonts.subheading,
                fontWeight: 'bold',
                fontSize: calcRem(20),
                lineHeight: calcRem(24),
                letterSpacing: '-0.44px',
            },
            sm: {
                fontFamily: scheme.fonts.subheading,
                fontWeight: 'bold',
                fontSize: calcRem(22),
                lineHeight: calcRem(32),
                letterSpacing: '-0.484px',
            },
            lg: {
                fontFamily: scheme.fonts.subheading,
                fontWeight: 'bold',
                fontSize: calcRem(25),
                lineHeight: calcRem(32),
                letterSpacing: '-0.55px',
            },
        },
        item: {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(20),
                lineHeight: calcRem(24),
                letterSpacing: '-0.44px',
            },
            sm: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(22),
                lineHeight: calcRem(32),
                letterSpacing: '-0.484px',
            },
            lg: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 'bold',
                fontSize: calcRem(25),
                lineHeight: calcRem(32),
                letterSpacing: '-0.55px',
            },
        },
        'item-small': {
            default: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(16),
                lineHeight: calcRem(24),
                letterSpacing: '-0.32px',
            },
            sm: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(18),
                lineHeight: calcRem(24),
                letterSpacing: '-0.36px',
            },
            lg: {
                fontFamily: scheme.fonts.heading,
                fontWeight: 600,
                fontSize: calcRem(20),
                lineHeight: calcRem(28),
                letterSpacing: '-0.4px',
            },
        },
        'body-alt': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(16),
                lineHeight: calcRem(24),
            },
        },
        'byline-primary': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 600,
                fontSize: calcRem(12),
                lineHeight: calcRem(16),
            },
        },
        'byline-secondary': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(12),
                lineHeight: calcRem(16),
            },
        },
        'button-label': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 600,
                fontSize: calcRem(16),
                lineHeight: calcRem(24),
                textTransform: 'uppercase',
            },
        },
        'byline-primary-large': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 500,
                fontSize: calcRem(16),
                lineHeight: calcRem(24),
            },
        },
        'menu-item': {
            default: {
                fontFamily: scheme.fonts.sansSerif,
                fontWeight: 'normal',
                fontSize: calcRem(20),
                lineHeight: calcRem(24),
            },
        },
    }
    return theme
}
